import difference from 'lodash/difference';
import intersection from 'lodash/intersection';
import { Fragment, ReactNode } from 'react';
import { Folder } from 'react-feather';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Event, Project as DefaultProject, Ticket } from '../../Shared/types';
import { Card } from '../../Shared/UI/Card';
import { Checkbox } from '../../Shared/UI/Checkbox';
import { FadeIn } from '../../Shared/UI/FadeIn';
import InputDescription from '../../Shared/UI/InputDescription';
import InputGroup from '../../Shared/UI/InputGroup';
import InputLabel from '../../Shared/UI/InputLabel';
import Picker from '../../Shared/UI/Picker';
import { SharedProps } from '../types';
import Container from '../UI/Container';
import Layout from '../UI/Layout';
import { EmbedHeader } from './EmbedLayout';
import EmbedScriptsCard from './EmbedScriptsCard';

interface EmbedFundsRaisedPageProps extends SharedProps {
  projects: (DefaultProject & {
    event: (Event & {
      tickets: Ticket[];
    }) | null;
  })[];
}

type Project = EmbedFundsRaisedPageProps['projects'][0];

export default function EmbedFundsRaisedPage({ projects, charity }: EmbedFundsRaisedPageProps) {
  const { t } = useTranslation();

  const { control, register, watch, setValue } = useForm({
    mode: 'all',
    defaultValues: {
      projects: [{ value: null }],
      tickets: [] as string[],
      includeIndividuals: true,
      includeTeams: true,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'projects',
  });

  const { includeIndividuals, includeTeams, projects: projectsValue, tickets } = watch();

  const selectedProjects = projectsValue.map((item) => (
    projects.filter((project) => project.id === item.value)[0]
  )).filter((project) => project);

  const getTickets = (project: Project) => project.event?.tickets || [];
  const getSelectedTicketIds = (project: Project) => (
    intersection(tickets, getTickets(project).map((ticket) => ticket.id))
  );

  const hasProjectWithTickets = selectedProjects.filter(
    (project) => getTickets(project).length > 0
  ).length > 0;

  const projectIds = selectedProjects.map((project) => project.id);
  const ticketIds = includeIndividuals ? selectedProjects.reduce((tickets, project) => ([
    ...tickets,
    ...getSelectedTicketIds(project),
  ]), [] as string[]) : [] as string[];

  const embedProps = {
    'data-charity': charity.id,
    'data-projects': projectIds.join(','),
    'data-tickets': ticketIds.join(','),
    'data-include-individuals': includeIndividuals ? null : false,
    'data-include-teams': includeTeams ? null : false,
  };

  const embedPropsString = Object.keys(embedProps).map((key) => {
    const value = embedProps[key as keyof typeof embedProps];

    if (value !== null && value !== '') {
      return `${key}="${value}"`;
    }

    return null;
  }).filter((value) => value).join(' ');

  const elementSnippet = `<span class="supporta-funds-raised" ${embedPropsString}></span>`;

  const handleProjectChange = () => {
    if (projectsValue[projectsValue.length - 1].value) {
      append({ value: null });
    }
  };

  const endpoint = `${window.location.protocol}//${window.location.host}/api`;

  return (
    <div>
      <EmbedHeader>
        <h1>
          {t('backend:funds_raised_embed_title')}
        </h1>
        <p>
          {t('backend:funds_raised_embed_description')}
        </p>
      </EmbedHeader>

      <Container>
        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-8">
          <div>
            <Card panels header>
              <div>
                <div className="flex items-center space-x-4 leading-tight">
                  <span className="flex items-center justify-center w-10 h-10 font-medium text-blue-700 bg-blue-200 rounded-full">
                    1
                  </span>
                  <div>
                    <h3 className="flex items-center">
                      {t('backend:setup')}
                    </h3>
                    <p className="text-slate-500">
                      {t('backend:setup_embed_description')}
                    </p>
                  </div>
                </div>
              </div>
              <form className="space-y-6">
                <InputGroup>
                  <InputLabel htmlFor="embed_projects">
                    {t('backend:project_filters')}
                  </InputLabel>
                  <InputDescription>
                    {t('backend:embed_project_filters_description')}
                  </InputDescription>
                  <div className="space-y-2">
                    {fields.map((field, index) => (
                      <Controller
                        control={control}
                        name={`projects.${index}.value`}
                        key={field.id}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <Picker
                            selected={value}
                            items={projects.filter((project) => (
                              project.id === projectsValue[index].value || !projectIds.includes(project.id)
                            ))}
                            onChange={(value) => {
                              if (value) {
                                onChange(value.id);
                                handleProjectChange();
                              } else {
                                remove(index);
                              }
                            }}
                            onBlur={onBlur}
                            icon={<Folder />}
                            optional
                            id={index === fields.length - 1 ? 'embed_projects' : undefined}
                          />
                        )}
                      />
                    ))}
                  </div>
                </InputGroup>

                <InputGroup>
                  <InputLabel>
                    {t('backend:campaign_types')}
                  </InputLabel>
                  <InputLabel htmlFor="embed_include_individuals" className="flex space-x-2 font-normal">
                    <Checkbox
                      {...register('includeIndividuals')}
                      id="embed_include_individuals"
                      className="mt-1"
                    />
                    <span>
                      {t('backend:individuals')}
                    </span>
                  </InputLabel>
                  {includeIndividuals && hasProjectWithTickets && selectedProjects.map((project) => (
                    <Fragment key={project.id}>
                      {selectedProjects.length > 1 && (
                        <InputLabel className="ml-4">
                          {project.title}
                        </InputLabel>
                      )}
                      <InputLabel htmlFor={`embed_all_tickets_${project.id}`} className="flex ml-4 space-x-2 font-normal">
                        <Checkbox
                          onClick={() => setValue('tickets', difference(tickets, getSelectedTicketIds(project)))}
                          disabled={getSelectedTicketIds(project).length === 0}
                          checked={getSelectedTicketIds(project).length === 0}
                          id={`embed_all_tickets_${project.id}`}
                          className="mt-1"
                        />
                        <span>
                          {t('backend:all_individuals')}
                        </span>
                      </InputLabel>
                      {getTickets(project).map((ticket) => (
                        <InputLabel htmlFor={`embed_ticket_${ticket.id}`} className="flex ml-4 space-x-2 font-normal" key={ticket.id}>
                          <Checkbox
                            {...register('tickets')}
                            value={ticket.id}
                            id={`embed_ticket_${ticket.id}`}
                            className="mt-1"
                          />
                          <span>
                            {ticket.title}
                          </span>
                        </InputLabel>
                      ))}
                    </Fragment>
                  ))}
                  <InputLabel htmlFor="embed_include_teams" className="flex space-x-2 font-normal">
                    <Checkbox
                      {...register('includeTeams')}
                      id="embed_include_teams"
                      className="mt-1"
                    />
                    <span>
                      {t('backend:teams')}
                    </span>
                  </InputLabel>
                </InputGroup>
              </form>
              <div className="bg-slate-50">
                <h3 className="mb-4">
                  {t('backend:preview')}
                </h3>
                <div className="text-xl" style={{ minHeight: 28 }}>
                  <FadeIn
                    className="supporta-funds-raised"
                    {...embedProps}
                    data-endpoint={endpoint}
                    key={embedPropsString}
                  />
                </div>
              </div>
            </Card>
          </div>
          <div>
            <EmbedScriptsCard elementSnippet={elementSnippet} />
          </div>
        </div>
      </Container>
    </div>
  );
}

EmbedFundsRaisedPage.layout = (page: ReactNode) => <Layout>{page}</Layout>;
